import { useEffect, useState, useCallback, useRef } from "react";
import {
  Droplets, Plus, RefreshCw, X, Loader2, AlertCircle, Search, Printer,
  CheckCircle2, Play, ClipboardCheck, Award, ArrowLeft, Trash2, Filter, Download,
  FileText, Upload, Link2,
} from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import ContainerNumberScanner from "../components/ContainerNumberScanner";
import AddAsPartnerButton from "../components/AddAsPartnerButton";
import { downloadCleaningCertificatePdf, downloadPrepCertificatePdf, downloadWashWorkOrderPdf, downloadCsv, codeFor } from "../lib/exporters";
import WashThroughput from "./WashThroughput";
import WaterSolutionDialog from "../components/WaterSolutionDialog";
import WaterSolutionsHistory from "../components/WaterSolutionsHistory";

const STATUS_PILL = {
  REQUESTED: { cls: "ps-pill ps-pill-neutral", l: "Requested" },
  IN_PROGRESS: { cls: "ps-pill ps-pill-warning", l: "In progress" },
  COMPLETED: { cls: "ps-pill ps-pill-success", l: "Completed" },
  CERTIFIED: { cls: "ps-pill ps-pill-success", l: "Certified" },
  HISTORICAL: { cls: "ps-pill ps-pill-neutral", l: "Historical" },
  CANCELLED: { cls: "ps-pill ps-pill-error", l: "Cancelled" },
};
function Pill({ status }) {
  const p = STATUS_PILL[status] || { cls: "ps-pill ps-pill-neutral", l: status };
  return <span className={p.cls}><span className="dot" />{p.l}</span>;
}

function fmtTime(t) { return t ? new Date(t).toLocaleString() : "—"; }
function todayLocal() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function CreateWashDialog({ open, onClose, onCreated, procedures }) {
  const empty = () => ({
    tank_number: "", iso_code: "22T6", tank_type: "ISO Tank Container", tank_size: "20FT",
    wash_type: "", _type_manually_set: false,
    eir_no: "", customer: "", customer_code: "", customer_address: "", customer_email: "", customer_attn: "",
    cleaned_for: "", place_of_cleaning: "Tema, Ghana",
    last_cargo: "", last_cargo_chem_name: "", last_cargo_un_no: "",
    last_cargo_2: "", last_cargo_chem_name_2: "", last_cargo_un_no_2: "",
    last_cargo_3: "", last_cargo_chem_name_3: "", last_cargo_un_no_3: "",
    imo_class: "",
    cleaning_procedures: [], cleaning_date: "", inspection_date: "",
    seals: { man_lid: "", top_discharge_valve: "", airline: "", bottom_outlet: "" },
    notes: "",
  });
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cargoSuggestions, setCargoSuggestions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [rateCard, setRateCard] = useState(null);
  const [suggestedType, setSuggestedType] = useState(null);
  useEffect(() => { if (open) { setForm(empty()); setErr(""); setSuggestedType(null); } }, [open]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/wash/rate-card")
      .then(({ data }) => { if (!cancelled) setRateCard(data); })
      .catch(() => { /* selector falls back to auto-classification */ });
    api.get("/wash/cargo-suggestions?limit=60")
      .then(({ data }) => { if (!cancelled) setCargoSuggestions(data?.items || []); })
      .catch(() => { /* non-blocking — chips just won't render */ });
    api.get("/partners?fields=code,legal_name")
      .then(({ data }) => { if (!cancelled) setPartners(Array.isArray(data) ? data : []); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [open]);

  // Auto-suggest the wash type from tank/customer/cargo — never overrides a
  // manual pick. Mirrors the backend heuristic via GET /wash/classify.
  useEffect(() => {
    if (!open || !rateCard) return;
    const { tank_number, customer, cleaned_for, last_cargo } = form;
    if (!tank_number && !customer && !cleaned_for && !last_cargo) return;
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (tank_number) params.append("tank_number", tank_number);
      if (customer) params.append("customer", customer);
      if (cleaned_for) params.append("cleaned_for", cleaned_for);
      if (last_cargo) params.append("last_cargo", last_cargo);
      api.get(`/wash/classify?${params.toString()}`)
        .then(({ data }) => {
          const s = data?.suggested;
          if (!s || s === "UNKNOWN") { setSuggestedType(null); return; }
          if (!rateCard.types.some((x) => x.code === s)) { setSuggestedType(null); return; }
          setSuggestedType(s);
          setForm((f) => (f._type_manually_set ? f : { ...f, wash_type: s }));
        })
        .catch(() => {});
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rateCard, form.tank_number, form.customer, form.cleaned_for, form.last_cargo]);

  // ── Lock the customer to the tank's gate-IN carrier ──────────────────
  // The gate event is the source of truth. If this tank is currently in the
  // yard (last_status == IN), its `last_carrier_code` wins — the operator
  // cannot override it. If the tank is OUT (or unknown), the customer field
  // stays free text as before. Fires on tank_number change.
  const [lockedCarrier, setLockedCarrier] = useState(null);
  useEffect(() => {
    if (!open) { setLockedCarrier(null); return; }
    const cn = (form.tank_number || "").toUpperCase().trim();
    if (!cn || cn.length < 4) { setLockedCarrier(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/containers/${encodeURIComponent(cn)}`);
        const c = data?.container || {};
        if (c.last_status === "IN" && c.last_carrier_code) {
          const code = (c.last_carrier_code || "").toUpperCase();
          const partner = partners.find((p) => (p.code || "").toUpperCase() === code);
          setLockedCarrier({
            code,
            legal_name: partner?.legal_name || code,
            last_customer: c.last_customer || null,
          });
          // Only overwrite if the user hasn't manually corrected it to a
          // different partner — but auto-fill when the field is empty or
          // mismatched. Avoids tagging the wash to the wrong carrier.
          setForm((f) => {
            if (f.customer_code === code) return f;
            return {
              ...f,
              customer: partner?.legal_name || code,
              customer_code: code,
              customer_address: f.customer_address || partner?.address?.line1 || "",
              customer_email: f.customer_email || partner?.billing_email || "",
            };
          });
        } else {
          setLockedCarrier(null);
        }
      } catch {
        setLockedCarrier(null);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.tank_number, partners]);

  // Fill the next empty cargo slot (or slot 1 if all full) with a quick-pick.
  const pickCargo = (c) => {
    setForm((f) => {
      const slots = ["", "_2", "_3"];
      const target = slots.find((s) => !(f[`last_cargo${s}`] || f[`last_cargo_chem_name${s}`])) || "";
      return {
        ...f,
        [`last_cargo${target}`]:          (c.trade || "").toUpperCase(),
        [`last_cargo_chem_name${target}`]: c.chem || "",
        [`last_cargo_un_no${target}`]:     c.un   || "",
      };
    });
  };
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProc = (p) => setForm((f) => ({
    ...f,
    cleaning_procedures: f.cleaning_procedures.includes(p)
      ? f.cleaning_procedures.filter((x) => x !== p)
      : [...f.cleaning_procedures, p],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (rateCard && !form.wash_type) {
        setErr("Pick a wash type — it's the fallback price used when no cargo product override applies.");
        setBusy(false);
        return;
      }
      const { _type_manually_set: _m, ...formForApi } = form;
      const payload = {
        ...formForApi,
        tank_number: form.tank_number.toUpperCase().trim(),
        wash_type: form.wash_type || null,
        cleaning_date: form.cleaning_date ? new Date(form.cleaning_date).toISOString() : null,
        inspection_date: form.inspection_date ? new Date(form.inspection_date).toISOString() : null,
      };
      const { data } = await api.post("/wash-orders", payload);
      if (data?.already_existed) {
        // Gate-in's "needs washing" tick auto-creates a REQUESTED wash. The
        // operator doesn't need a duplicate — hand them the existing one.
        toast.info(`Tank ${data.tank_number} already has an open wash (${data.wash_no}) — opening it.`);
        onCreated?.(data); onClose?.();
        return;
      }
      toast.success(`Wash order ${data.wash_no} created — printing work order…`);
      // Auto-download the cleaner's work order so it's ready to hand to them
      try { downloadWashWorkOrderPdf(data); } catch (_) { /* ignore PDF errors */ }
      onCreated?.(data); onClose?.();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="create-wash-dialog">
      <div className="bg-white max-w-5xl w-full mt-8 rounded-sm shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Wash Station · New order</div>
            <h2 className="font-display text-xl font-extrabold">Request tank wash</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          {err && (
            <div className="border border-red-200 bg-red-50 text-red-900 text-sm p-3 flex items-start gap-2" data-testid="wash-create-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><div>{err}</div>
            </div>
          )}

          {/* Tank + customer */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Tank number *</label>
              <div className="flex gap-2">
                <input required value={form.tank_number} onChange={(e) => set("tank_number", e.target.value.toUpperCase())} className="ps-input font-mono flex-1" placeholder="MTNU1260982" data-testid="wash-tank" />
                <ContainerNumberScanner dataTestid="wash-ocr" small onResult={(cn) => set("tank_number", cn)} />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">ISO code</label>
              <input value={form.iso_code} onChange={(e) => set("iso_code", e.target.value.toUpperCase())} className="ps-input font-mono" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Tank size</label>
              <input value={form.tank_size} onChange={(e) => set("tank_size", e.target.value)} className="ps-input font-mono" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">EIR No (if linked)</label>
              <input value={form.eir_no} onChange={(e) => set("eir_no", e.target.value)} className="ps-input font-mono" placeholder="EIR-GHTEM-…" />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                Customer *
                {form.customer_code && !lockedCarrier && (
                  <span className="ml-2 normal-case tracking-normal font-normal text-emerald-700" data-testid="wash-customer-code-pill">
                    · partner <span className="font-mono font-bold">{form.customer_code}</span>
                  </span>
                )}
              </label>
              <select
                required
                value={form.customer_code || ""}
                disabled={!!lockedCarrier}
                onChange={(e) => {
                  const code = e.target.value;
                  const partner = partners.find((p) => p.code === code);
                  setForm((f) => ({
                    ...f,
                    customer_code: code,
                    customer: partner?.legal_name || partner?.trading_name || code,
                    customer_address: f.customer_address || partner?.address?.line1 || "",
                    customer_email: f.customer_email || partner?.billing_email || "",
                  }));
                }}
                className={`ps-input ${lockedCarrier ? "bg-gray-100 text-gray-700 cursor-not-allowed" : ""}`}
                data-testid="wash-customer"
                title={lockedCarrier ? `Locked to the carrier recorded on gate-in (${lockedCarrier.code}). To change the carrier, gate the tank OUT and back IN under the correct carrier.` : "Pick the invoicing customer. If missing, create them in Partners first."}
              >
                <option value="">— Select customer —</option>
                {partners.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.code}{p.legal_name && p.legal_name !== p.code ? ` · ${p.legal_name}` : ""}
                  </option>
                ))}
              </select>
              {lockedCarrier && (
                <div className="text-[11px] mt-1 flex items-start gap-1.5 text-[#006E9E]" data-testid="wash-customer-locked">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Locked to <b className="font-mono">{lockedCarrier.code}</b> — the carrier recorded at gate-in on this tank.
                    To change it, gate the tank OUT and back IN under the correct carrier.
                  </span>
                </div>
              )}
              {!lockedCarrier && !form.customer_code && (
                <div className="text-[11px] text-gray-500 mt-1">
                  Can't find the customer? <a href="/partners?new=1" target="_blank" rel="noreferrer" className="underline">Create them in Partners</a> first.
                </div>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Cleaned for</label>
              <input value={form.cleaned_for} onChange={(e) => set("cleaned_for", e.target.value)} className="ps-input" placeholder="Chemsolv" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Place of cleaning</label>
              <input value={form.place_of_cleaning} onChange={(e) => set("place_of_cleaning", e.target.value)} className="ps-input" />
            </div>
            <div className="col-span-2 sm:col-span-2">
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Customer address</label>
              <input value={form.customer_address} onChange={(e) => set("customer_address", e.target.value)} className="ps-input" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Customer attn</label>
              <input value={form.customer_attn} onChange={(e) => set("customer_attn", e.target.value)} className="ps-input" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Customer email</label>
              <input type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="ps-input" />
            </div>
          </div>

          {/* Wash type — required, drives the price from the depot rate card */}
          {rateCard && (
            <div className="border border-blue-200 bg-blue-50/40 p-4" data-testid="wash-type-section">
              <div className="flex items-baseline justify-between mb-3">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-900">
                  Wash type <span className="text-red-500">*</span>
                  <span className="ml-2 normal-case tracking-normal font-normal text-gray-500">
                    fallback price when no cargo product override applies —{" "}
                    <a href="/cargo-products" className="text-blue-700 underline hover:no-underline">manage</a>
                  </span>
                </div>
                {suggestedType && form.wash_type === suggestedType && !form._type_manually_set && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300 rounded-sm" data-testid="wash-type-suggested">
                    Auto-suggested — click another to override
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {rateCard.types.map((t) => {
                  const active = form.wash_type === t.code;
                  return (
                    <button
                      key={t.code} type="button"
                      onClick={() => setForm((f) => ({ ...f, wash_type: t.code, _type_manually_set: true }))}
                      data-testid={`wash-type-${t.code}`}
                      className={`px-3 py-2.5 rounded-sm border-2 text-left transition-colors ${
                        active ? "bg-[#006E9E] text-white border-[#006E9E]" : "bg-white text-gray-700 border-gray-200 hover:border-[#006E9E]/50"
                      }`}
                    >
                      <div className="text-sm font-bold leading-tight">{t.label}</div>
                      <div className={`text-xs font-mono mt-0.5 ${active ? "opacity-80" : "text-gray-500"}`}>
                        {rateCard.currency} {Number(t.price).toFixed(2)} <span className="opacity-60">· {t.code}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cargo */}
          <div className="border border-gray-200 bg-gray-50 p-4">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-3">Last products carried</div>
            <p className="text-[11px] text-gray-500 mb-3 -mt-2">
              Enter the <strong>trade/commercial name</strong> on the left (e.g. "Palm Oil RBD") and the <strong>IUPAC chemical name</strong> in the middle (e.g. "Triglyceride mix"). If they're the same, leave the chemical column blank.
            </p>

            {/* Quick-pick chips — one tap fills the next empty cargo slot */}
            {cargoSuggestions.length > 0 && (
              <div className="mb-4 -mt-1" data-testid="cargo-quickpick">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600">Quick pick</span>
                  <span className="text-[10px] text-gray-400">
                    tap to fill next empty slot ·
                    <span className="ml-1">chips marked <span className="font-mono font-bold text-blue-700">★</span> come from the Cargo Products library and lock in the invoice rate</span>
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
                  {cargoSuggestions.map((c, i) => {
                    const isProduct = c.source === "product";
                    const tone = c.family === "FOOD"
                      ? "border-[#99D420]/60 bg-[#99D420]/10 hover:bg-[#99D420]/20 text-[#3a6500]"
                      : c.family === "CHEM"
                      ? "border-[#F1471D]/60 bg-[#F1471D]/10 hover:bg-[#F1471D]/20 text-[#9b2a0f]"
                      : "border-gray-300 bg-white hover:bg-gray-100 text-gray-700";
                    // Product-backed chips get a slightly stronger border so
                    // operators visually prefer them — same category tone,
                    // just amped up. Rate preview appears in the title tooltip.
                    const productAccent = isProduct ? "ring-1 ring-blue-500/40" : "";
                    const ratePreview = isProduct && c.default_rate
                      ? ` · will invoice at ${c.default_currency || "USD"} ${c.default_rate.toFixed(2)}`
                      : "";
                    return (
                      <button
                        key={`${c.trade}-${i}`}
                        type="button"
                        onClick={() => pickCargo(c)}
                        title={
                          (c.chem ? `${c.trade}${c.un ? ` · UN ${c.un}` : ""} — ${c.chem}` : c.trade)
                          + ratePreview
                          + (isProduct ? " · from Cargo Products library" : "")
                        }
                        className={`text-[11px] font-mono px-2 py-1 border rounded-sm transition ${tone} ${productAccent}`}
                        data-testid={`cargo-chip-${c.trade.replace(/[^A-Z0-9]/gi, "")}`}
                      >
                        {isProduct && <span className="text-blue-700 mr-0.5">★</span>}
                        {c.trade}
                        {c.un && <span className="ml-1 text-[10px] opacity-70">UN {c.un}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {cargoSuggestions.length > 0 && (
              <datalist id="cargo-trade-list">
                {cargoSuggestions.map((c) => (
                  <option key={c.trade} value={c.trade}>{c.chem ? `${c.chem}${c.un ? ` · UN ${c.un}` : ""}` : c.un ? `UN ${c.un}` : ""}</option>
                ))}
              </datalist>
            )}

            {[1, 2, 3].map((n) => {
              const sfx = n === 1 ? "" : `_${n}`;
              const trade = form[`last_cargo${sfx}`] || "";
              const chem  = form[`last_cargo_chem_name${sfx}`] || "";
              const dup = trade && chem && trade.trim().toUpperCase() === chem.trim().toUpperCase();
              return (
                <div key={n} className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                      Product {n > 1 ? n : ""} {n === 1 && <span className="font-normal normal-case text-gray-400">(trade name)</span>}
                    </label>
                    <input value={trade} onChange={(e) => set(`last_cargo${sfx}`, e.target.value.toUpperCase())} className="ps-input font-mono" placeholder="e.g. PALM OIL RBD" list="cargo-trade-list" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                      Chemical / IUPAC name {dup && <span className="font-normal normal-case text-amber-700 ml-1">— same as trade name?</span>}
                    </label>
                    <input value={chem} onChange={(e) => set(`last_cargo_chem_name${sfx}`, e.target.value)} className={`ps-input ${dup ? "border-amber-400 bg-amber-50" : ""}`} placeholder="leave blank if same as product name" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">UN #</label>
                    <input value={form[`last_cargo_un_no${sfx}`] || ""} onChange={(e) => set(`last_cargo_un_no${sfx}`, e.target.value)} className="ps-input font-mono" />
                  </div>
                </div>
              );
            })}
            <div className="grid grid-cols-4 gap-3 mt-2">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">IMO class</label>
                <input value={form.imo_class} onChange={(e) => set("imo_class", e.target.value)} className="ps-input font-mono" placeholder="3 / 6.1 / 8 …" />
              </div>
            </div>
          </div>

          {/* Procedures — grouped FOOD / CHEM / COMMON */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-2">Cleaning procedures (pick all that apply)</div>
            {(() => {
              const groups = { FOOD: [], CHEM: [], COMMON: [] };
              for (const p of procedures) {
                const name = typeof p === "string" ? p : p.name;
                const family = (typeof p === "string" ? "COMMON" : p.family) || "COMMON";
                groups[family]?.push(p);
              }
              const META = {
                FOOD:   { label: "Food-grade only",        accent: "#99D420", bg: "bg-[#99D420]/10" },
                CHEM:   { label: "Chemical-grade only",    accent: "#F1471D", bg: "bg-[#F1471D]/10" },
                COMMON: { label: "Common (either)",        accent: "#006E9E", bg: "bg-[#006E9E]/5"  },
              };
              return (
                <div className="space-y-3">
                  {["FOOD", "CHEM", "COMMON"].map((fam) => groups[fam].length > 0 && (
                    <div
                      key={fam}
                      className={`border-l-4 ${META[fam].bg} pl-3 py-2`}
                      style={{ borderLeftColor: META[fam].accent }}
                      data-testid={`proc-family-${fam.toLowerCase()}`}
                    >
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: META[fam].accent }}>
                        {META[fam].label}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {groups[fam].map((p) => {
                          const name = typeof p === "string" ? p : p.name;
                          const code = typeof p === "string" ? null : p.code;
                          const on = form.cleaning_procedures.includes(name);
                          return (
                            <button
                              key={name} type="button" onClick={() => toggleProc(name)}
                              className={`px-3 py-2 border text-sm transition-colors ${on ? "bg-[#006E9E] text-white border-[#006E9E]" : "border-gray-200 bg-white hover:border-gray-400"}`}
                              data-testid={`proc-${name}`}
                            >
                              {on ? "✓ " : ""}
                              {code && (
                                <span className={`font-mono text-[10px] mr-1 px-1 py-0.5 rounded-sm ${on ? "bg-white/20 text-white" : "bg-[#FEF3C6] text-[#7B3306]"}`}>{code}</span>
                              )}
                              {name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Seals are applied only after the wash is done — captured on the
              "Mark completed" step instead of blocking order creation. */}
          <div className="border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-2" data-testid="wash-seals-deferred-note">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <b>Seal numbers are recorded later</b> — when the wash is marked
              <b> Completed</b> (or at certification). You can create this work
              order now without seals.
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="ps-btn ps-btn-primary" data-testid="submit-wash">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><CheckCircle2 className="w-4 h-4" /> Create wash order</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdvanceWashDialog({ wash, target, onClose, onDone }) {
  // Used for IN_PROGRESS → COMPLETED and COMPLETED → CERTIFIED transitions
  // so operators can back-date the event (e.g. weekend washes entered
  // on Monday) and, for FOOD-grade washes, record the ATP test result that
  // must appear on the cleaning certificate.
  const [when, setWhen] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isFood = wash?.wash_category === "FOOD";
  const isComplete = target?.status === "COMPLETED";
  // ATP test is captured on COMPLETED but never blocks the transition — even
  // FOOD-grade operators can finish without a reading for now (toggle here +
  // backend guard in routes/wash.py to re-enable enforcement).
  const [atpRlu, setAtpRlu] = useState("");
  const [atpThreshold, setAtpThreshold] = useState(30);
  const [atpTestedBy, setAtpTestedBy] = useState("");
  // Post-clean seals — applied once the wash is done, so they're captured
  // here (COMPLETED / CERTIFIED) instead of at wash-order creation.
  const [seals, setSeals] = useState({
    man_lid: wash?.seals?.man_lid || "",
    top_discharge_valve: wash?.seals?.top_discharge_valve || "",
    airline: wash?.seals?.airline || "",
    bottom_outlet: wash?.seals?.bottom_outlet || "",
  });
  useEffect(() => {
    if (target) {
      setSeals({
        man_lid: wash?.seals?.man_lid || "",
        top_discharge_valve: wash?.seals?.top_discharge_valve || "",
        airline: wash?.seals?.airline || "",
        bottom_outlet: wash?.seals?.bottom_outlet || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!target) return null;
  const showAtp = isComplete;
  const showSeals = isComplete || target?.status === "CERTIFIED";
  const atpInt = parseInt(atpRlu, 10);
  const atpPass = Number.isFinite(atpInt) ? atpInt <= atpThreshold : null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setBusy(true);
    try {
      const body = {
        status: target.status,
        when: when ? new Date(when).toISOString() : null,
      };
      if (showAtp && Number.isFinite(atpInt)) {
        body.atp_rlu = atpInt;
        body.atp_threshold = atpThreshold;
        body.atp_pass = atpPass;
        body.atp_tested_by = atpTestedBy.trim() || null;
        body.atp_tested_at = when ? new Date(when).toISOString() : null;
      }
      if (showSeals && Object.values(seals).some((v) => (v || "").trim())) {
        body.seals = seals;
      }
      await api.post(`/wash-orders/${wash.id}/status`, body);
      toast.success(`Status → ${target.status}`);
      onDone?.();
      onClose?.();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="advance-wash-dialog">
      <div className="bg-white max-w-xl w-full mt-12 rounded-sm shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Wash Order</div>
            <h2 className="font-display text-lg font-extrabold">{target.label}</h2>
            <div className="text-xs text-gray-500 mt-1 font-mono">{wash.wash_no} · {wash.tank_number}{isFood && <span className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300">FOOD</span>}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900" data-testid="advance-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          {err && (
            <div className="border border-red-200 bg-red-50 text-red-900 text-sm p-3 flex items-start gap-2" data-testid="advance-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><div>{err}</div>
            </div>
          )}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
              {isComplete ? "Cleaning date / time" : "Inspection date / time"} <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="ps-input font-mono"
              required
              data-testid="advance-when"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Set to when the {isComplete ? "wash physically finished" : "inspection took place"} — defaults to now, edit if you&apos;re back-dating.
            </p>
          </div>

          {showAtp && (
            <div className="border border-gray-200 bg-gray-50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600">
                  ATP Hygiene Test <span className="text-gray-400 normal-case tracking-normal font-normal">(optional)</span>
                </div>
                {Number.isFinite(atpInt) && (
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border ${
                      atpPass
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-red-100 text-red-800 border-red-300"
                    }`}
                    data-testid="atp-result-pill"
                  >
                    {atpPass ? "PASS" : "FAIL"} · {atpInt} RLU
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">RLU value</label>
                  <input
                    type="number"
                    min="0"
                    max="99999"
                    value={atpRlu}
                    onChange={(e) => setAtpRlu(e.target.value)}
                    placeholder="e.g. 12"
                    className="ps-input font-mono"
                    data-testid="atp-rlu"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Threshold (≤ pass)</label>
                  <input
                    type="number"
                    min="1"
                    max="9999"
                    value={atpThreshold}
                    onChange={(e) => setAtpThreshold(parseInt(e.target.value, 10) || 30)}
                    className="ps-input font-mono"
                    data-testid="atp-threshold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Tested by</label>
                  <input
                    value={atpTestedBy}
                    onChange={(e) => setAtpTestedBy(e.target.value)}
                    placeholder="Technician name"
                    className="ps-input"
                    data-testid="atp-tested-by"
                  />
                </div>
              </div>
              <p className="text-[11px] text-gray-500">
                ATP measures surface contamination via Relative Light Units. A reading at or below {atpThreshold} RLU passes. Result is printed on the cleaning certificate when recorded.{isFood && " Recommended for FOOD-grade washes."}
              </p>
            </div>
          )}

          {showSeals && (
            <div className="border border-gray-200 bg-gray-50 p-4 space-y-3" data-testid="advance-seals-section">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600">
                Seal numbers <span className="text-gray-400 normal-case tracking-normal font-normal">(post-clean · optional — fill what's applied)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["man_lid", "Man lid"],
                  ["top_discharge_valve", "Top discharge valve"],
                  ["airline", "Airline"],
                  ["bottom_outlet", "Bottom outlet"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">{label}</label>
                    <input
                      value={seals[k]}
                      onChange={(e) => setSeals((s) => ({ ...s, [k]: e.target.value.toUpperCase() }))}
                      className="ps-input font-mono"
                      placeholder="—"
                      data-testid={`advance-seal-${k}`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-500">
                Seals go on once the wash is finished — leave blank if not yet applied; you can add them at certification.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="advance-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="ps-btn ps-btn-primary" data-testid="advance-submit">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><target.icon className="w-4 h-4" /> {target.label}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SealBreakButton({ wash, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    position: "man_lid",
    replacement_seal: "",
    reason: "",
    broken_by: "",
    broken_at: new Date().toISOString().slice(0, 10),
  });

  const submit = async () => {
    if (!form.reason || form.reason.trim().length < 3) {
      toast.error("Reason is required.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        position: form.position,
        replacement_seal: form.replacement_seal.trim() || null,
        reason: form.reason.trim(),
        broken_by: form.broken_by.trim() || null,
        broken_at: form.broken_at ? `${form.broken_at}T00:00:00` : null,
      };
      await api.post(`/wash-orders/${wash.id}/seal-break`, body);
      toast.success("Seal break recorded — it will show on the certificate.");
      setOpen(false);
      setForm((f) => ({ ...f, replacement_seal: "", reason: "" }));
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const currentSeal = wash.seals?.[form.position] || "";

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="seal-break-open"
        className="ps-btn ps-btn-secondary h-7 text-[11px]">
        <AlertCircle className="w-3 h-3" /> Log seal break
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="seal-break-dialog" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white border border-gray-200 max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <div className="text-sm font-bold text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Log seal break
              </div>
              <button onClick={() => setOpen(false)} disabled={busy} className="text-gray-400 hover:text-gray-700">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Position</label>
                <select value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
                  className="ps-input w-full" data-testid="seal-break-position">
                  <option value="man_lid">Man lid</option>
                  <option value="top_discharge_valve">Top discharge valve</option>
                  <option value="airline">Airline</option>
                  <option value="bottom_outlet">Bottom outlet</option>
                </select>
                <div className="text-[11px] text-gray-500 mt-1 font-mono">Current seal: {currentSeal || "—"}</div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Replacement seal (optional)</label>
                <input value={form.replacement_seal} onChange={(e) => setForm((f) => ({ ...f, replacement_seal: e.target.value.toUpperCase() }))}
                  placeholder="Leave blank if no new seal applied"
                  className="ps-input w-full font-mono" data-testid="seal-break-replacement" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Reason <span className="text-red-600">*</span></label>
                <textarea value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                  rows={3} placeholder="e.g. Buffer tank inspection requested by KIVO client"
                  className="ps-input w-full" data-testid="seal-break-reason" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Technician</label>
                  <input value={form.broken_by} onChange={(e) => setForm((f) => ({ ...f, broken_by: e.target.value }))}
                    placeholder="Who broke the seal"
                    className="ps-input w-full" data-testid="seal-break-by" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Date</label>
                  <input type="date" value={form.broken_at} onChange={(e) => setForm((f) => ({ ...f, broken_at: e.target.value }))}
                    className="ps-input w-full" data-testid="seal-break-date" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
              <button onClick={() => setOpen(false)} disabled={busy} className="ps-btn ps-btn-secondary h-9">Cancel</button>
              <button onClick={submit} disabled={busy} data-testid="seal-break-submit"
                className="ps-btn ps-btn-primary h-9 bg-red-700 hover:bg-red-800">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                Record seal break
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StcPoLinker({ wash, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [showCandidates, setShowCandidates] = useState(false);
  const [manualPo, setManualPo] = useState("");
  const fileRef = useRef(null);

  const currentPo = wash.stc_po_number || "";
  const isStolt = (wash.customer || "").toUpperCase().includes("STOLT");

  const lookup = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/wash-orders/${wash.id}/stc-po-candidates`);
      setCandidates(r.data.candidates || []);
      setShowCandidates(true);
      if (!r.data.candidates?.length) {
        toast.info("No STC PO candidates found for this tank in the last 90 days.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setPo = async (po, mv) => {
    setBusy(true);
    try {
      await api.post(`/wash-orders/${wash.id}/stc-po`, {
        stc_po_number: po || null,
        stc_move_number: mv || null,
      });
      toast.success(po ? `Linked STC PO ${po}` : "STC PO cleared");
      setShowCandidates(false);
      setManualPo("");
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onPickFile = () => fileRef.current?.click();
  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await api.post(`/wash-orders/${wash.id}/attach-stc-pdf`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const x = r.data.extracted;
      toast.success(`PO ${x.po_number} linked (${x.tank_count} tank${x.tank_count===1?"":"s"} in packet)`);
      if (r.data.warning) toast.warning(r.data.warning, { duration: 8000 });
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border border-gray-200 bg-white" data-testid="stc-po-linker">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#006E9E]" />
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#006E9E]">
            STC Cleaning Instruction
          </div>
        </div>
        {!isStolt && (
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Optional · non-Stolt wash</span>
        )}
      </div>
      <div className="p-5 text-sm space-y-3">
        {currentPo ? (
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">STC PO</div>
              <div className="font-mono font-bold text-base text-[#006E9E]" data-testid="stc-po-value">{currentPo}</div>
            </div>
            {wash.stc_move_number && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Move No.</div>
                <div className="font-mono text-sm">{wash.stc_move_number}</div>
              </div>
            )}
            {wash.stc_po_linked_by && (
              <div className="text-[11px] text-gray-500">
                linked by {wash.stc_po_linked_by.split("@")[0]} · {wash.stc_po_linked_at ? new Date(wash.stc_po_linked_at).toLocaleDateString() : ""}
              </div>
            )}
            <button onClick={() => setPo("", "")} disabled={busy}
              className="ml-auto text-[11px] text-red-600 hover:underline" data-testid="stc-po-clear">
              Clear
            </button>
          </div>
        ) : (
          <div className="text-gray-500 text-[13px]">
            No STC PO linked. Accounts will need this at invoicing time — link it now.
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
          <button onClick={lookup} disabled={busy}
            className="ps-btn ps-btn-secondary h-8 text-xs" data-testid="stc-po-lookup">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            Search received instructions
          </button>
          <button onClick={onPickFile} disabled={busy}
            className="ps-btn ps-btn-secondary h-8 text-xs" data-testid="stc-po-upload-pdf">
            <Upload className="w-3.5 h-3.5" /> Upload STC PDF
          </button>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf"
            onChange={onFileChosen} className="hidden" data-testid="stc-po-upload-input" />

          <div className="flex items-center gap-1 ml-auto">
            <input value={manualPo} onChange={(e) => setManualPo(e.target.value)}
              placeholder="Enter PO manually"
              className="ps-input h-8 text-xs font-mono w-40" data-testid="stc-po-manual-input" />
            <button onClick={() => setPo(manualPo.trim(), "")}
              disabled={busy || !manualPo.trim()}
              className="ps-btn ps-btn-primary h-8 text-xs" data-testid="stc-po-manual-save">
              <Link2 className="w-3.5 h-3.5" /> Link
            </button>
          </div>
        </div>

        {showCandidates && candidates && candidates.length > 0 && (
          <div className="border border-gray-200 bg-gray-50 mt-2" data-testid="stc-po-candidates-list">
            <div className="px-3 py-2 border-b border-gray-200 text-[10px] uppercase tracking-wider font-bold text-gray-600 flex items-center justify-between">
              <span>{candidates.length} candidate{candidates.length===1?"":"s"} for {wash.tank_number}</span>
              <button onClick={() => setShowCandidates(false)} className="text-gray-400 hover:text-gray-700">
                <X className="w-3 h-3" />
              </button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-white">
                <tr><th className="px-2 py-1 text-left">PO</th><th className="px-2 py-1 text-left">Product</th><th className="px-2 py-1 text-left">Received</th><th className="px-2 py-1"></th></tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.po_number} className="border-t border-gray-200" data-testid={`stc-po-candidate-${c.po_number}`}>
                    <td className="px-2 py-1.5 font-mono font-bold text-[#006E9E]">{c.po_number}</td>
                    <td className="px-2 py-1.5">{c.product || c.previous_cargo || "—"}</td>
                    <td className="px-2 py-1.5 text-gray-500">{c.received_at ? new Date(c.received_at).toLocaleDateString() : "—"}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button onClick={() => setPo(c.po_number, c.move_number)}
                        disabled={busy}
                        className="ps-btn ps-btn-primary h-6 text-[10px] px-2" data-testid={`stc-po-pick-${c.po_number}`}>
                        Use
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function WashDetail({ wash, onBack, onRefresh, canDelete }) {
  const [advanceTarget, setAdvanceTarget] = useState(null);
  const [startRef, setStartRef] = useState(null); // {open, ref}

  const advance = async (target) => {
    // REQUESTED → IN_PROGRESS now prompts for the carrier reference number
    // (Stolt schedule no. / customer booking) before the transition fires.
    if (target.status === "IN_PROGRESS") {
      setStartRef({ open: true, ref: wash.ref_no || "" });
      return;
    }
    setAdvanceTarget(target);
  };

  const submitStartRef = async () => {
    const ref = (startRef?.ref || "").trim();
    const isStolt = (wash.customer || "").toUpperCase().includes("STOLT");
    if (isStolt && !ref) {
      toast.error("Carrier ref is required for Stolt washes.");
      return;
    }
    try {
      await api.post(`/wash-orders/${wash.id}/status`, {
        status: "IN_PROGRESS",
        ref_no: ref || undefined,
      });
      toast.success(`Status → IN_PROGRESS${ref ? ` (ref ${ref})` : ""}`);
      setStartRef(null);
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete wash order ${wash.wash_no}?`)) return;
    try {
      await api.delete(`/wash-orders/${wash.id}`);
      toast.success("Deleted");
      onBack?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const next = {
    REQUESTED: { status: "IN_PROGRESS", label: "Start washing", icon: Play },
    IN_PROGRESS: { status: "COMPLETED", label: "Mark completed", icon: ClipboardCheck },
    COMPLETED: { status: "CERTIFIED", label: "Issue certificate", icon: Award },
  }[wash.status];

  return (
    <div className="space-y-4" data-testid="wash-detail">
      <div className="flex items-center justify-between gap-2">
        <button className="ps-btn ps-btn-secondary" onClick={onBack} data-testid="wash-back">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </button>
        <div className="flex gap-2">
          <button className="ps-btn ps-btn-secondary" onClick={() => downloadWashWorkOrderPdf(wash)} data-testid="wash-print-workorder">
            <Printer className="w-4 h-4" /> Print work order
          </button>
          {wash.cleaning_cert_no && (
            <button className="ps-btn ps-btn-primary" onClick={() => downloadCleaningCertificatePdf(wash)} data-testid="wash-print-cert">
              <Printer className="w-4 h-4" /> Print certificate
            </button>
          )}
          {wash.is_prep_package && (
            <button className="ps-btn ps-btn-primary" onClick={() => downloadPrepCertificatePdf(wash)} data-testid="wash-print-prep-cert">
              <Printer className="w-4 h-4" /> Prep Cert
            </button>
          )}
          {next && (
            <button className="ps-btn ps-btn-primary" onClick={() => advance(next)} data-testid={`advance-${next.status}`}>
              <next.icon className="w-4 h-4" /> {next.label}
            </button>
          )}
          {canDelete && (
            <button className="ps-btn ps-btn-secondary text-red-600" onClick={remove} data-testid="wash-delete">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
      </div>

      <div className="border border-gray-200 bg-white">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Wash Order</div>
            <div className="font-mono font-bold text-lg">{wash.wash_no}</div>
            <div className="mt-1"><Pill status={wash.status} /></div>
            {wash.cleaning_cert_no && (
              <div className="font-mono text-sm mt-1 text-[#006E9E]">Cert: {wash.cleaning_cert_no}</div>
            )}
          </div>
          <div className="text-right text-sm">
            <div className="font-mono font-bold text-lg">{wash.tank_number}</div>
            <div className="text-xs text-gray-500 font-mono">{wash.iso_code} · {wash.tank_size}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 divide-x divide-gray-200">
          <div className="p-5 text-sm space-y-2">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#006E9E]">Customer</div>
            <KV k="Customer" v={wash.customer} />
            <KV k="Address" v={wash.customer_address} />
            <KV k="Attn" v={wash.customer_attn} />
            <KV k="Email" v={wash.customer_email} />
            <KV k="Cleaned for" v={wash.cleaned_for} />
            <KV k="Place" v={wash.place_of_cleaning} />
            <KV k="EIR No" v={wash.eir_no} />
          </div>
          <div className="p-5 text-sm space-y-2">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#006E9E]">Cargo</div>
            {/* Show a cargo row whenever ANY field (trade name, chem name, or UN number)
                is populated. Previously this row was gated on the trade name only, so
                operators who filled the chem name but skipped the trade name saw an
                empty cargo block — even though the data was in the database. */}
            {(() => {
              const fmtCargo = (trade, chem, un) => {
                const name = (trade || chem || "").trim();
                const detail = (trade && chem && chem.trim().toUpperCase() !== trade.trim().toUpperCase()) ? ` — ${chem}` : "";
                return `${name}${detail} (UN ${un || "—"})`;
              };
              const hasCargo1 = wash.last_cargo || wash.last_cargo_chem_name || wash.last_cargo_un_no;
              const hasCargo2 = wash.last_cargo_2 || wash.last_cargo_chem_name_2 || wash.last_cargo_un_no_2;
              const hasCargo3 = wash.last_cargo_3 || wash.last_cargo_chem_name_3 || wash.last_cargo_un_no_3;
              return (
                <>
                  <KV k="Last cargo" v={hasCargo1 ? fmtCargo(wash.last_cargo, wash.last_cargo_chem_name, wash.last_cargo_un_no) : "—"} />
                  {hasCargo2 && <KV k="Cargo II" v={fmtCargo(wash.last_cargo_2, wash.last_cargo_chem_name_2, wash.last_cargo_un_no_2)} />}
                  {hasCargo3 && <KV k="Cargo III" v={fmtCargo(wash.last_cargo_3, wash.last_cargo_chem_name_3, wash.last_cargo_un_no_3)} />}
                </>
              );
            })()}
            <KV k="IMO" v={wash.imo_class} />
            <KV k="Carrier ref" v={wash.ref_no || <span className="text-gray-400">—</span>} />
            <KV k="Cleaning date" v={fmtTime(wash.cleaning_date)} />
            <KV k="Inspection date" v={fmtTime(wash.inspection_date)} />
            {wash.atp_rlu !== undefined && wash.atp_rlu !== null && (
              <KV
                k="ATP test"
                v={
                  <span className="font-mono">
                    {wash.atp_rlu} RLU{" "}
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ml-1 ${
                        wash.atp_pass
                          ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                          : "bg-red-100 text-red-800 border-red-300"
                      }`}
                      data-testid="wash-atp-pill"
                    >
                      {wash.atp_pass ? "PASS" : "FAIL"}
                    </span>
                    {wash.atp_threshold && (
                      <span className="text-gray-400 ml-2 text-[11px]">(≤ {wash.atp_threshold} RLU)</span>
                    )}
                  </span>
                }
              />
            )}
          </div>
        </div>
      </div>

      <StcPoLinker wash={wash} onRefresh={onRefresh} />

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border border-gray-200 bg-white p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600 mb-3">Cleaning procedures</div>
          {(wash.cleaning_procedures || []).length === 0
            ? <div className="text-gray-400 text-sm">—</div>
            : <ol className="space-y-1 text-sm">
                {(wash.cleaning_procedures || []).map((p, i) => {
                  const code = codeFor(p);
                  return (
                    <li key={i} className="flex items-center gap-2">
                      <span className="text-gray-400 font-mono text-xs w-5 text-right">{i + 1}.</span>
                      {code && (
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FEF3C6] text-[#7B3306] border border-[#F1471D]/20">{code}</span>
                      )}
                      <span className="font-mono">{p}</span>
                    </li>
                  );
                })}
              </ol>}
        </div>
        <div className="border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600">Seal numbers (post-clean)</div>
            <SealBreakButton wash={wash} onDone={onRefresh} />
          </div>
          <ol className="space-y-1 text-sm font-mono">
            {[
              ["man_lid", "Man lid"],
              ["top_discharge_valve", "Top discharge valve"],
              ["airline", "Airline"],
              ["bottom_outlet", "Bottom outlet"],
            ].map(([k, label], i) => {
              const broken = (wash.seal_breaks || []).filter((b) => b.position === k);
              return (
                <li key={k}>
                  {i + 1}. {label}: <span className="font-bold">{wash.seals?.[k] || "—"}</span>
                  {broken.length > 0 && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-300" data-testid={`seal-broken-${k}`}>
                      ⚠ broken {broken.length > 1 ? `· ${broken.length}×` : ""}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
          {(wash.seal_breaks || []).length > 0 && (
            <div className="mt-4 pt-3 border-t border-red-200" data-testid="seal-breaks-list">
              <div className="text-[10px] uppercase tracking-wider font-bold text-red-700 mb-2">
                Seal break record ({wash.seal_breaks.length})
              </div>
              <div className="space-y-2">
                {wash.seal_breaks.map((b) => (
                  <div key={b.id} className="text-xs border-l-2 border-red-400 pl-2 py-0.5">
                    <div className="font-medium">
                      {{
                        man_lid: "Man lid",
                        top_discharge_valve: "Top discharge valve",
                        airline: "Airline",
                        bottom_outlet: "Bottom outlet",
                      }[b.position] || b.position}
                      {" · "}{b.broken_at ? new Date(b.broken_at).toLocaleDateString("en-GB") : "—"}
                      {" · by "}{b.broken_by || "—"}
                    </div>
                    <div className="text-gray-600 font-mono">
                      Original {b.original_seal || "—"}{b.replacement_seal ? ` → replaced with ${b.replacement_seal}` : " (no replacement)"}
                    </div>
                    <div className="text-gray-500 italic">"{b.reason}"</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {wash.wash_category === "FOOD" && <PrepPackagePanel wash={wash} onChange={onRefresh} />}

      <AdvanceWashDialog
        wash={wash}
        target={advanceTarget}
        onClose={() => setAdvanceTarget(null)}
        onDone={onRefresh}
      />

      {startRef?.open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="start-wash-dialog">
          <div className="bg-white max-w-md w-full mt-12 rounded-sm shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Start Wash</div>
              <h2 className="font-display text-lg font-extrabold">{wash.wash_no} · {wash.tank_number}</h2>
              <div className="text-xs text-gray-500 mt-1">{wash.customer}</div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
                  Carrier reference {(wash.customer || "").toUpperCase().includes("STOLT") && <span className="text-red-500">*</span>}
                </label>
                <input
                  value={startRef.ref}
                  onChange={(e) => setStartRef((s) => ({ ...s, ref: e.target.value }))}
                  placeholder={(wash.customer || "").toUpperCase().includes("STOLT") ? "Stolt schedule no. (e.g. 11036866)" : "Optional"}
                  className="ps-input font-mono"
                  data-testid="start-wash-ref"
                  autoFocus
                />
                <p className="text-[11px] text-gray-500 mt-1">Required for Stolt — prints on the cleaning certificate and the wash invoice line item.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button className="ps-btn ps-btn-secondary" onClick={() => setStartRef(null)} data-testid="start-wash-cancel">Cancel</button>
                <button className="ps-btn ps-btn-primary" onClick={submitStartRef} data-testid="start-wash-submit">
                  <Play className="w-4 h-4" /> Start washing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const GASKET_TYPES = [
  { v: "WHITE_EPDM",     l: "White EPDM (FDA food-grade)" },
  { v: "PTFE_ENVELOPE",  l: "PTFE Envelope" },
  { v: "SILICONE",       l: "Silicone" },
  { v: "BLACK_EPDM",     l: "Black EPDM (general)" },
  { v: "VITON",          l: "Viton" },
];

function PrepPackagePanel({ wash, onChange }) {
  const prep = wash.prep_package || {};
  const enabled = !!wash.is_prep_package;
  const editable = !["CERTIFIED", "HISTORICAL", "CANCELLED"].includes(wash.status);
  const [form, setForm] = useState({
    enabled,
    gasket_type: prep.gasket_type || "WHITE_EPDM",
    gaskets_replaced: prep.gaskets_replaced || ["MANLID", "BOTTOM_DISCHARGE", "AIRLINE"],
    pressure_test_result: prep.pressure_test_result || "",
    test_pressure_bar: prep.test_pressure_bar ?? 0.7,
    seal_manlid: prep.seal_manlid || "",
    seal_airline: prep.seal_airline || "",
    seal_discharge: prep.seal_discharge || "",
    technician: prep.technician || "",
    notes: prep.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGasket = (g) => setForm((f) => ({
    ...f, gaskets_replaced: f.gaskets_replaced.includes(g)
      ? f.gaskets_replaced.filter((x) => x !== g)
      : [...f.gaskets_replaced, g],
  }));

  const save = async (newEnabled = form.enabled) => {
    setSaving(true);
    try {
      await api.patch(`/wash-orders/${wash.id}/prep`, { ...form, enabled: newEnabled });
      toast.success(newEnabled ? "Prep Package saved" : "Prep Package removed");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-[#006E9E]/30 bg-white" data-testid="prep-package-panel">
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#006E9E]">
            Clean &amp; Prep Package <span className="ml-2 text-gray-400 normal-case tracking-normal">— Food-grade only</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Wash + gasket replacement + pneumatic leak test + tamper-evident seals → combined certificate.</div>
        </div>
        {editable && (
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => { set("enabled", e.target.checked); save(e.target.checked); }}
              data-testid="prep-enabled-toggle"
              className="w-4 h-4"
            />
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-gray-700">Enable</span>
          </label>
        )}
        {!editable && enabled && (
          <span className="ps-pill ps-pill-success"><span className="dot" /> PREP ENABLED</span>
        )}
      </div>

      {(form.enabled || enabled) && (
        <div className="p-5 grid sm:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Gasket type *</label>
              <select
                className="ps-input font-mono w-full"
                value={form.gasket_type}
                onChange={(e) => set("gasket_type", e.target.value)}
                disabled={!editable}
                data-testid="prep-gasket-type"
              >
                {GASKET_TYPES.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Gaskets replaced</label>
              <div className="flex gap-3 text-xs">
                {["MANLID", "BOTTOM_DISCHARGE", "AIRLINE"].map((g) => (
                  <label key={g} className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.gaskets_replaced.includes(g)}
                      onChange={() => toggleGasket(g)}
                      disabled={!editable}
                      data-testid={`prep-gasket-${g}`}
                    />
                    <span className="font-mono">{g.replace("_", " ")}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Pneumatic test pressure (bar) *</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="10"
                className="ps-input font-mono w-32"
                value={form.test_pressure_bar}
                onChange={(e) => set("test_pressure_bar", parseFloat(e.target.value) || 0)}
                disabled={!editable}
                data-testid="prep-test-pressure"
              />
              <span className="ml-2 text-xs text-gray-500">Typical 0.5 – 1.0 bar</span>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Pressure test result *</label>
              <div className="flex gap-2">
                {["PASS", "FAIL"].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => editable && set("pressure_test_result", r)}
                    disabled={!editable}
                    data-testid={`prep-test-${r.toLowerCase()}`}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] border-2 transition-colors ${
                      form.pressure_test_result === r
                        ? r === "PASS"
                          ? "border-[#99D420] bg-[#99D420]/15 text-[#3B5908]"
                          : "border-[#F1471D] bg-[#F1471D]/15 text-[#7B3306]"
                        : "border-gray-300 text-gray-500 hover:border-gray-400"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600">Tamper-evident seal numbers *</div>
            {[
              { k: "seal_manlid",    l: "Manlid seal" },
              { k: "seal_airline",   l: "Airline seal" },
              { k: "seal_discharge", l: "Discharge seal" },
            ].map((s) => (
              <div key={s.k}>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">{s.l}</label>
                <input
                  type="text"
                  className="ps-input font-mono w-full"
                  value={form[s.k]}
                  onChange={(e) => set(s.k, e.target.value.toUpperCase())}
                  disabled={!editable}
                  placeholder="e.g. PS-2026-04-1234"
                  data-testid={`prep-${s.k.replace("_", "-")}`}
                />
              </div>
            ))}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Technician initials</label>
              <input
                type="text"
                className="ps-input font-mono w-32"
                value={form.technician}
                onChange={(e) => set("technician", e.target.value.toUpperCase())}
                disabled={!editable}
                data-testid="prep-technician"
              />
            </div>
            {editable && (
              <div className="pt-2">
                <button
                  className="ps-btn ps-btn-primary"
                  onClick={() => save(true)}
                  disabled={saving}
                  data-testid="prep-save"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><ClipboardCheck className="w-4 h-4" /> Save Prep Package</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <dt className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500 self-center">{k}</dt>
      <dd className="col-span-2 font-mono text-sm">{v || <span className="text-gray-400">—</span>}</dd>
    </div>
  );
}

export default function WashStation() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  const canEditPartners = user?.role === "admin" || user?.role === "supervisor";
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [partnerCodes, setPartnerCodes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", tank_number: "", customer: "" });
  const [showCreate, setShowCreate] = useState(false);
  const [showWaterSolution, setShowWaterSolution] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("queue");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const [list, sum] = await Promise.all([
        api.get(`/wash-orders?${params.toString()}`),
        api.get("/wash/summary"),
      ]);
      setItems(list.data); setSummary(sum.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/wash/procedures").then((r) => setProcedures(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/partners?fields=code")
      .then((r) => setPartnerCodes(new Set((r.data || []).map((p) => p.code))))
      .catch(() => {});
  }, []);

  const exportCsv = () => {
    if (!items.length) return;
    downloadCsv(items, `wash_orders_${new Date().toISOString().slice(0,10)}`, [
      { key: "wash_no", label: "Wash No" },
      { key: "cleaning_cert_no", label: "Cert No" },
      { key: "tank_number", label: "Tank" },
      { key: "iso_code", label: "ISO" },
      { key: "customer", label: "Customer" },
      { key: "cleaned_for", label: "Cleaned For" },
      { key: "last_cargo_chem_name", label: "Last Cargo" },
      { key: "last_cargo_un_no", label: "UN #" },
      { key: "status", label: "Status" },
      { key: "cleaning_date", label: "Cleaning Date" },
      { key: "created_at", label: "Created" },
    ]);
  };

  if (selected) {
    return (
      <div data-testid="wash-page">
        <PageHeader title="Wash Order Detail" subtitle={selected.wash_no} testid="wash-detail-header" />
        <div className="p-6 sm:p-8">
          <WashDetail
            wash={selected}
            canDelete={canDelete}
            onBack={() => { setSelected(null); load(); }}
            onRefresh={async () => {
              const { data } = await api.get(`/wash-orders/${selected.id}`);
              setSelected(data);
            }}
          />
        </div>
      </div>
    );
  }

  const kpis = [
    { l: "Requested", v: summary?.requested ?? "—", cls: "text-gray-700" },
    { l: "In progress", v: summary?.in_progress ?? "—", cls: "text-amber-600" },
    { l: "Completed", v: summary?.completed ?? "—", cls: "text-emerald-600" },
    { l: "Certified", v: summary?.certified ?? "—", cls: "text-[#006E9E]" },
    { l: "Historical", v: summary?.historical ?? "—", cls: "text-gray-500" },
  ];

  return (
    <div data-testid="wash-page">
      <PageHeader
        title="Tank Wash Station"
        subtitle="Tank cleaning queue — interior inspection, wash according to spec, cleaning certificate."
        testid="wash-header"
        actions={
          <>
            <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="refresh-wash">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button className="ps-btn ps-btn-secondary" disabled={!items.length} onClick={exportCsv} data-testid="export-wash-csv">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button className="ps-btn ps-btn-secondary" onClick={() => setShowWaterSolution(true)} data-testid="new-water-solution" title="Log a Water Solution service (tank + PO + flat fee)">
              <Droplets className="w-4 h-4" /> Water Solution
            </button>
            <button className="ps-btn ps-btn-primary" onClick={() => setShowCreate(true)} data-testid="new-wash">
              <Plus className="w-4 h-4" /> New wash order
            </button>
          </>
        }
      />

      <div className="px-6 sm:px-8 pt-4 border-b border-gray-200 bg-white flex gap-1">
        {[
          { v: "queue", l: "Queue" },
          { v: "throughput", l: "Throughput" },
          { v: "water", l: "Water Solutions" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            data-testid={`wash-tab-${t.v}`}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors ${
              tab === t.v ? "border-[#006E9E] text-[#006E9E]" : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
          >{t.l}</button>
        ))}
      </div>

      {tab === "throughput" ? (
        <div className="p-6 sm:p-8"><WashThroughput /></div>
      ) : tab === "water" ? (
        <div className="p-6 sm:p-8"><WaterSolutionsHistory onLogNew={() => setShowWaterSolution(true)} /></div>
      ) : (
      <div className="p-6 sm:p-8 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="wash-kpis">
          {kpis.map((k) => (
            <div key={k.l} className="border border-gray-200 bg-white p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{k.l}</div>
              <div className={`font-mono font-black text-3xl mt-1 ${k.cls}`}>{k.v}</div>
            </div>
          ))}
        </div>

        <div className="border border-gray-200 bg-white p-4 grid sm:grid-cols-3 gap-3" data-testid="wash-filters">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input placeholder="Tank number" value={filters.tank_number}
              onChange={(e) => setFilters({ ...filters, tank_number: e.target.value.toUpperCase() })}
              className="ps-input font-mono pl-9" data-testid="filter-tank" />
          </div>
          <div className="relative">
            <Filter className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input placeholder="Customer" value={filters.customer}
              onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              className="ps-input pl-9" data-testid="filter-customer" />
          </div>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="ps-input" data-testid="filter-status">
            <option value="">All statuses</option>
            {Object.keys(STATUS_PILL).map((s) => <option key={s} value={s}>{STATUS_PILL[s].l}</option>)}
          </select>
        </div>

        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="ps-table" data-testid="wash-table">
            <thead>
              <tr>
                <th>Wash No</th>
                <th>Cert No</th>
                <th>Tank</th>
                <th>Customer</th>
                <th>Cleaned for</th>
                <th>Last cargo</th>
                <th>STC PO</th>
                <th>Procedures</th>
                <th>Status</th>
                <th>Cleaning date</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="text-center text-gray-400 py-10">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={11} className="text-center text-gray-400 py-10">No wash orders. Click <strong>New wash order</strong>.</td></tr>
              )}
              {!loading && items.map((w) => (
                <tr key={w.id} data-testid={`wash-row-${w.wash_no}`}>
                  <td className="font-mono font-bold">{w.wash_no}</td>
                  <td className="font-mono text-xs text-[#006E9E]">{w.cleaning_cert_no || <span className="text-gray-400">—</span>}</td>
                  <td className="font-mono">{w.tank_number}</td>
                  <td className="text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <span>{w.customer}</span>
                      <AddAsPartnerButton
                        name={w.customer}
                        role="customer"
                        canEdit={canEditPartners}
                        knownCodes={partnerCodes}
                      />
                    </span>
                  </td>
                  <td className="text-xs">{w.cleaned_for || "—"}</td>
                  <td className="text-xs font-mono">{w.last_cargo_chem_name || w.last_cargo || "—"}</td>
                  <td className="text-xs font-mono" data-testid={`stc-po-cell-${w.wash_no}`}>
                    {w.stc_po_number
                      ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#E6F1F7] text-[#006E9E] border border-[#006E9E]/30 font-bold" title={`Move ${w.stc_move_number || ""}`}>{w.stc_po_number}</span>
                      : (((w.customer || "").toUpperCase().includes("STOLT"))
                          ? <span className="text-amber-600 font-bold" title="Stolt wash without STC PO — will block invoicing">⚠ missing</span>
                          : <span className="text-gray-300">—</span>)}
                  </td>
                  <td className="text-xs">{(w.cleaning_procedures || []).length || "—"}</td>
                  <td><Pill status={w.status} />{w.is_prep_package && <span className="ml-1 ps-pill ps-pill-success text-[10px]" data-testid={`prep-badge-${w.wash_no}`}>PREP</span>}</td>
                  <td className="text-xs text-gray-500 font-mono" data-testid={`wash-cleaning-date-${w.wash_no}`}>{w.cleaning_date ? new Date(w.cleaning_date).toLocaleDateString() : (w.created_at ? new Date(w.created_at).toLocaleDateString() : "—")}</td>
                  <td className="text-right">
                    <div className="inline-flex gap-1">
                      <button className="ps-btn ps-btn-secondary h-7 px-2 text-xs" onClick={() => setSelected(w)} data-testid={`view-wash-${w.wash_no}`}>
                        View
                      </button>
                      <button className="ps-btn ps-btn-secondary h-7 px-2 text-xs" onClick={() => downloadWashWorkOrderPdf(w)} data-testid={`workorder-wash-${w.wash_no}`} title="Print work order for cleaner">
                        <Printer className="w-3.5 h-3.5" /> WO
                      </button>
                      {w.cleaning_cert_no && (
                        <button className="ps-btn ps-btn-primary h-7 px-2 text-xs" onClick={() => downloadCleaningCertificatePdf(w)} data-testid={`print-wash-${w.wash_no}`}>
                          <Printer className="w-3.5 h-3.5" /> Cert
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <CreateWashDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(data) => {
          // When the backend returned an existing wash (gate-in auto-created
          // it already), jump straight into the detail view so the operator
          // can update/cancel it instead of being blocked by a duplicate.
          if (data?.already_existed) setSelected(data);
          load();
        }}
        procedures={procedures}
      />
      <WaterSolutionDialog
        open={showWaterSolution}
        onClose={() => setShowWaterSolution(false)}
        onLogged={load}
      />
    </div>
  );
}
